/**
 * Immersive presentation: models at true size, on a floor you are standing on.
 *
 * This is the mode the whole app exists for. The scale factor is not a look — it is a
 * measurement, so that judging whether a part will fit is something you can do by holding
 * your hand up to it. Judging whether two parts fit *each other* is the same question
 * asked of a pair, which is why the room holds as many models as you care to put in it.
 *
 * Three's WebXR manager defaults to a `local-floor` reference space, which puts y = 0 at
 * the real floor. That is what makes "rest the model on the ground" mean the actual
 * ground, and it degrades to an estimated eye-height offset on hardware that cannot
 * provide it.
 *
 * Either the trigger or the grip picks up whichever model the hand is nearest, and the
 * trigger picks out one that is out of reach instead — three jobs on that button, sorted
 * by what the hand is near enough to do. The upper face button summons the menu and lets go
 * of whatever is being held, which keeps clicking the menu from ever overlapping with the
 * rest.
 *
 * The sticks split the work by hand: the right one moves a model about the room, the left
 * one turns it where it stands, and either pressed in resizes.
 *
 * ## The room outlives the session
 *
 * What is in the room, and where each model has been put, is kept here rather than read
 * back off the scene when needed. The desktop mode normalises whatever model is selected
 * and thinks nothing of overwriting its transform, so an arrangement left in the scene
 * would not survive taking the headset off and putting it back on. Saving it on the way
 * out and restoring it on the way in is what makes the room still be there when you
 * return to it.
 */

import {
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Line,
  LineBasicMaterial,
  type Object3D,
  Quaternion,
  Vector3,
} from 'three';
import { XRControllerModelFactory } from 'three/addons/webxr/XRControllerModelFactory.js';

import {
  nudgePosition,
  nudgeRotation,
  nudgeScale,
  settleRatio,
  TRUE_SIZE,
  type Stick,
} from '../domain/nudge.js';
import {
  boundsSize,
  computeRoomPlacement,
  footprintRadius,
  MM_TO_METRES,
  type Occupancy,
} from '../domain/scaling.js';
import { Selection } from '../domain/selection.js';
import type { Bounds, ModelId, ModelMeta, Quat, Vec3 } from '../domain/types.js';
import { GrabController } from './grab.js';
import {
  ButtonWatcher,
  isButtonPressed,
  readStick,
  THUMBSTICK_BUTTON,
  UPPER_FACE_BUTTON,
} from './input.js';
import { ModelPointer, Outline } from './pointing.js';
import type { PresentationMode, SceneManager, SceneModel } from './sceneManager.js';
import { VrMenu, type MenuHit, type VrMenuCallbacks } from './vrMenu.js';

const CONTROLLER_COUNT = 2;

/** Length of the pointer ray when it is not touching anything. */
const RAY_LENGTH_M = 1.5;

/** Strength and length of the buzz that says a model has landed back on its true size. */
const DETENT_PULSE = 0.4;
const DETENT_PULSE_MS = 40;

/**
 * A model in the room, and where it has been put.
 *
 * The transform is stored as plain numbers rather than as a reference to the live object,
 * so that it is a record of an arrangement rather than a view of whatever the scene
 * happens to hold right now.
 */
interface RoomMember {
  readonly model: SceneModel;
  position: Vec3;
  /** Models can be turned over as well as moved, by hand or by the left stick. */
  rotation: Quat;
  scale: number;
  /**
   * The multiple of true size last handed to the application to store.
   *
   * Kept so that a resize is reported once it is finished rather than sixty times a second
   * while it is happening, and so that a gesture ending where it started writes nothing.
   */
  reported: number;
}

/** What `VrMode` needs of the application, being the menu's needs plus one of its own. */
export interface VrModeCallbacks extends VrMenuCallbacks {
  /**
   * A model has finished being resized, at this multiple of its true size.
   *
   * Raised when the gesture ends, not while it runs: the point of the call is to persist
   * the figure, and a write per frame would be sixty writes a second for as long as
   * somebody held the stick.
   */
  readonly onResize: (id: ModelId, roomScale: number) => void;
}

/** A model a hand is aimed at, and how far off it was met. */
interface AimedModel {
  readonly member: RoomMember;
  readonly distance: number;
}

export class VrMode implements PresentationMode {
  /** The menu panel. Public because it is a component of this mode, not a secret of it. */
  readonly menu: VrMenu;

  private readonly grab: GrabController;
  private readonly buttons = new ButtonWatcher();
  private readonly pointer = new ModelPointer();

  private readonly controllers: Group[] = [];
  private readonly grips: Group[] = [];
  private readonly rays: Line[] = [];
  private readonly outlines: Outline[] = [];
  private readonly inputSources: (XRInputSource | undefined)[] = [];

  /**
   * What each hand is aimed at, sampled once a frame.
   *
   * Held between the two halves of the frame's work rather than probed again, so that the
   * model a stick moves is exactly the model the outline has just said it would.
   */
  private readonly aimed: (AimedModel | undefined)[] = [];

  /**
   * What each hand took hold of when its stick left centre, until the stick returns to it.
   *
   * Aim alone is not enough to move something by. A model pushed away travels along the
   * floor while a hand points slightly downwards at it, so within a frame or two the beam
   * has passed over the top of it and the model stops dead halfway to where it was being
   * sent. Latching means the hand chooses once and then drives, rather than having to
   * chase what it is steering.
   */
  private readonly latched: (RoomMember | undefined)[] = [];

  /**
   * Which model each hand has said it means, and for how much longer it goes on saying so.
   *
   * The box is drawn round this rather than round whatever the beam is crossing. Pointing
   * is not choosing: a hand at rest points somewhere, and a box that is up whenever the ray
   * happens to touch a model is a box that says nothing. Pulling the trigger at a model out
   * of reach says it, driving one with the stick says it, and taking hold of one directly
   * unsays it — what is in your hand needs no box round it.
   */
  private readonly selections = Array.from(
    { length: CONTROLLER_COUNT },
    () => new Selection(),
  );

  /** How long each hand has held its stick against the true-size detent. */
  private readonly dwell = [0, 0];

  /**
   * What has already been done to which model this frame.
   *
   * Two hands doing the same thing to one model would do it at double speed, which is a
   * puzzle rather than a feature. Keyed by the change as well as the model, because the two
   * hands no longer do the same thing: turning a model with one while pushing it with the
   * other is a reasonable thing to want, and only the clash is worth refusing.
   */
  private readonly claimed = new Set<string>();

  /** What is in the room, in the order it was put there. */
  private readonly room = new Map<ModelId, RoomMember>();

  // Scratch values for reading a transform back off a model, so saving the arrangement
  // allocates nothing.
  private readonly scratchPosition = new Vector3();
  private readonly scratchRotation = new Quaternion();
  private readonly scratchScale = new Vector3();

  private active = false;

  constructor(
    private readonly scene: SceneManager,
    private readonly callbacks: VrModeCallbacks,
  ) {
    this.grab = new GrabController(scene.scene);
    this.menu = new VrMenu(callbacks);
    this.scene.scene.add(this.menu.group);
    this.createControllers();
  }

  enter(): void {
    this.active = true;
    this.buttons.reset();
    this.forgetAim();
    this.scene.setEnvironmentVisible(true);
    this.restoreArrangement();
    // Nothing here shows the hands. Three does that, from the poses it is given — see
    // `hideHands` for why claiming otherwise leaves a spare one lying about.
  }

  exit(): void {
    this.active = false;
    // Hand the models back before the desktop mode tries to transform one, or it would be
    // repositioning something parented to a controller that no longer exists.
    this.grab.releaseAll();
    this.saveArrangement();

    // A headset taken off mid-push ends the gesture without the stick ever coming back to
    // centre, so this is the other place a resize can finish.
    for (const member of this.room.values()) this.reportScale(member);

    this.menu.close();
    this.buttons.reset();
    this.forgetAim();
    this.hideHands();
    this.scene.setEnvironmentVisible(false);
  }

  /**
   * Per-frame work: sample the buttons, update the pointers, act on the sticks.
   *
   * Grabbing is event-driven and needs nothing here. Everything else does: the face
   * buttons and the sticks are only exposed as polled gamepad state, the menu highlight
   * has to follow the ray, and a stick is a rate that has to be turned into a movement by
   * however long the frame took.
   *
   * Aiming comes before nudging on purpose. The outline drawn this frame is the promise
   * that the stick will move that model, and probing twice could break it.
   */
  update(deltaSeconds: number): void {
    if (!this.active) return;

    this.pollMenuButton();
    this.fadeSelections(deltaSeconds);
    this.updatePointers();
    this.nudgeAimed(deltaSeconds);
  }

  /**
   * Put a model in the room, in the first free spot on the arc in front of the viewer.
   *
   * Already being there is not an error: it is what a second press of the same menu row
   * would mean if the row were not a toggle, and the sensible answer is to leave it alone
   * rather than move it out from under someone.
   */
  /**
   * @param roomScale the multiple of true size it was last left at, from its record.
   */
  addToRoom(model: SceneModel, roomScale = TRUE_SIZE): void {
    if (this.room.has(model.id)) return;

    // Note where everything has got to before deciding where this one goes. Putting the
    // new model out means putting the others back, and "back" has to mean where they were
    // last left rather than where they first landed — otherwise adding a second model
    // undoes all the arranging done to the first.
    if (this.active) this.saveArrangement();

    // Placed at the size it will actually stand at, not at the size its file describes. A
    // model shrunk to a tenth needs a tenth of the floor, and one grown fivefold would
    // otherwise be stood on top of its neighbour.
    const size = shownSize(model.bounds, roomScale);
    const { scale, position } = computeRoomPlacement(size, this.occupancy());

    this.room.set(model.id, {
      model,
      position,
      rotation: [0, 0, 0, 1],
      scale: scale * roomScale,
      reported: roomScale,
    });

    if (this.active) this.restoreArrangement();
  }

  /**
   * Take a model out of the room. It stays in the library, and stays loaded.
   *
   * Its place is not filled in: the others stay where the user put them, because tidying
   * up around a removal would move models they had deliberately arranged.
   */
  removeFromRoom(id: ModelId): void {
    if (!this.room.delete(id)) return;

    // A hand holding it would otherwise keep it parented to the controller even though it
    // is no longer part of the room; `setTargets` lets go of anything it no longer lists.
    this.grab.setTargets(this.roots());

    if (!this.active) return;

    // A hand may have been steering the model that has just gone.
    this.forgetAim();

    // As in `addToRoom`: the survivors are about to be put back, so what they are put back
    // to had better be where they were last left.
    this.saveArrangement();
    this.restoreArrangement();
  }

  /**
   * Set how large a model in the room stands, without reporting it back.
   *
   * For a size decided in another session and reconciled into the record here. Recorded as
   * already reported, because it *is* what the record says — raising it as a change would
   * send it back where it came from.
   *
   * Applied to the object directly rather than through the arrangement, so that a model
   * being held at the time changes size in the hand instead of waiting to be let go of and
   * then quietly losing the change to the saved transform.
   */
  resizeInRoom(id: ModelId, roomScale: number): void {
    const member = this.room.get(id);
    if (!member) return;

    member.scale = MM_TO_METRES * roomScale;
    member.reported = roomScale;
    member.model.root.scale.setScalar(member.scale);
    member.model.root.updateMatrixWorld(true);

    if (this.active) this.scene.frameLighting();
  }

  isInRoom(id: ModelId): boolean {
    return this.room.has(id);
  }

  get roomIds(): readonly ModelId[] {
    return [...this.room.keys()];
  }

  get isRoomEmpty(): boolean {
    return this.room.size === 0;
  }

  /**
   * Keep the in-VR menu's list in step with the library.
   *
   * The menu marks which models are in the room rather than which one is selected: in a
   * headset the selection is not something you can see or act on, whereas what is standing
   * in front of you is the question the list is being consulted to answer.
   */
  setLibrary(models: readonly ModelMeta[]): void {
    this.menu.setLibrary(models, this.roomIds);
  }

  /**
   * Keep the in-VR menu's account status in step with the header button.
   *
   * Shown, not acted on: signing in happens in the desktop browser, where there is a real
   * keyboard. See `accountStatusLabel` in `menuLayout`.
   */
  setAccount(email: string | undefined): void {
    this.menu.setAccount(email);
  }

  /** Lay the room out again from scratch, undoing any arranging the user has done. */
  resetPlacement(): void {
    this.grab.releaseAll();
    this.forgetAim();

    const occupied: Occupancy[] = [];
    for (const member of this.room.values()) {
      // True size as well as the original spot. A resize is part of the arrangement this
      // is undoing, and leaving one model at three times its size while putting everything
      // else back would be a reset that did not reset.
      const size = boundsSize(member.model.bounds);
      const { scale, position } = computeRoomPlacement(size, occupied);

      member.position = position;
      member.rotation = [0, 0, 0, 1];
      member.scale = scale;
      occupied.push({ position, radius: footprintRadius(size) });

      this.reportScale(member);
    }

    this.restoreArrangement();
  }

  get isMenuOpen(): boolean {
    return this.menu.isOpen;
  }

  /** Where each model in the room stands, for placing the next one clear of them. */
  private occupancy(): Occupancy[] {
    return [...this.room.values()].map((member) => ({
      position: member.position,
      radius: footprintRadius(shownSize(member.model.bounds, ratioOf(member))),
    }));
  }

  private roots(): Object3D[] {
    return [...this.room.values()].map((member) => member.model.root);
  }

  /**
   * Put every model back where the room says it belongs, and show exactly those.
   *
   * Called on entry, and whenever the room changes while presenting. Models being held
   * are left alone: their transform is the hand's business until it lets go, and moving
   * one out from under a hand mid-grab is both jarring and immediately undone.
   */
  private restoreArrangement(): void {
    for (const member of this.room.values()) {
      const { root } = member.model;
      if (this.grab.isHeld(root)) continue;

      root.position.set(...member.position);
      root.quaternion.set(...member.rotation);
      root.scale.setScalar(member.scale);
      root.updateMatrixWorld(true);
    }

    this.scene.setVisible(this.room.keys());
    this.grab.setTargets(this.roots());
    this.pointer.setTargets(this.roots());
    this.scene.frameLighting();
  }

  /**
   * Record where everything has ended up.
   *
   * Read off the live objects rather than tracked as they move, because the moving is
   * done by re-parenting to a controller — there is no single place a movement passes
   * through to be noticed. Taken from the world matrix so that a model let go of while
   * attached to a hand is still recorded where it appears to be.
   */
  private saveArrangement(): void {
    for (const member of this.room.values()) {
      const { root } = member.model;
      root.updateMatrixWorld(true);
      root.matrixWorld.decompose(
        this.scratchPosition,
        this.scratchRotation,
        this.scratchScale,
      );

      member.position = [
        this.scratchPosition.x,
        this.scratchPosition.y,
        this.scratchPosition.z,
      ];
      member.rotation = [
        this.scratchRotation.x,
        this.scratchRotation.y,
        this.scratchRotation.z,
        this.scratchRotation.w,
      ];
      // Uniform throughout: the only scale ever applied is millimetres to metres.
      //
      // Put back onto true size when it is within a hair of it. Decomposing a matrix is
      // where a scale picks up the last-bit error `settleRatio` exists for, and this is
      // the one place a model that has never been resized passes through one.
      const ratio = settleRatio(this.scratchScale.x / MM_TO_METRES);
      member.scale = ratio === TRUE_SIZE ? MM_TO_METRES : this.scratchScale.x;
    }
  }

  /**
   * Toggle the menu when the upper face button is pressed on either controller.
   *
   * Reaching for the menu lets go of the model. Keeping hold of it across the switch
   * would mean tracking which button took it so that clicking a menu item did not drop
   * it, and the model only falls back into the scene where it already was — not worth
   * the bookkeeping.
   */
  private pollMenuButton(): void {
    for (let index = 0; index < CONTROLLER_COUNT; index += 1) {
      const gamepad = this.inputSources[index]?.gamepad ?? undefined;
      const pressed = isButtonPressed(gamepad, UPPER_FACE_BUTTON);

      if (this.buttons.justPressed(`menu-${index.toString()}`, pressed)) {
        this.grab.releaseAll();
        this.menu.toggle(this.scene.renderer.xr.getCamera());
      }
    }
  }

  /**
   * Let each hand's box time out.
   *
   * A selection is renewed by being acted on, so this only ever expires one that has been
   * left alone — a model pointed out and then not picked up, moved or resized.
   */
  private fadeSelections(seconds: number): void {
    if (seconds <= 0) return;
    for (const selection of this.selections) selection.tick(seconds);
  }

  /**
   * Work out what each hand is aimed at, and show it.
   *
   * The rays are always drawn, because with the menu shut they now mean something: they
   * are what says where a trigger pull or a stick push would land. The ray is shortened to
   * stop at whatever it meets, which is what makes it read as touching the thing rather
   * than passing through it.
   *
   * The menu takes precedence over the models while it is up. It is a panel hanging in
   * front of you, so pointing through it at a model behind is not a thing anyone means to
   * do, and the sticks stand down for as long as it is open.
   */
  private updatePointers(): void {
    const open = this.menu.isOpen;
    let pointer: MenuHit | undefined;

    for (const [index, controller] of this.controllers.entries()) {
      const ray = this.rays[index];
      if (ray) ray.visible = true;

      // Whatever the beam meets first, the ground included. The menu is a panel in front
      // of you and a model stands on the floor, so either is nearer than the ground
      // whenever the hand is pointing at one at all — but a hand pointing at neither is
      // usually pointing downwards, and there the floor is the only thing there is to stop
      // at.
      const ground = this.pointer.floorReach(controller);
      const reach = (distance: number | undefined): number =>
        Math.min(distance ?? RAY_LENGTH_M, ground);

      if (open) {
        this.aimed[index] = undefined;
        this.latched[index] = undefined;
        this.selections[index]?.clear();
        this.outlines[index]?.hide();

        const hit = this.menu.probe(controller);
        if (ray) ray.scale.z = reach(hit?.distance);

        // Whichever hand finds the panel first owns the cursor. The other still draws its
        // ray, but must not cancel out the highlight by reporting that it missed.
        pointer ??= hit;
        continue;
      }

      const aim = this.aimAt(controller);
      this.aimed[index] = aim;
      if (ray) ray.scale.z = reach(aim?.distance);

      this.showOutline(index);
    }

    if (open) this.menu.setPointer(pointer);
  }

  /**
   * Draw the box round whatever this hand has said it means, if anything.
   *
   * Not round what the beam is crossing: the ray already says where you are pointing, and
   * the box answers the different question of which model you have singled out. While a
   * stick is driving one they part company on purpose — the model travels on while the hand
   * points wherever it likes, and each of the two goes on telling the truth about its own
   * thing.
   *
   * A model taken into a hand loses its box outright rather than waiting to lapse. There is
   * nothing left to point out about something you are holding.
   */
  private showOutline(index: number): void {
    const outline = this.outlines[index];
    if (!outline) return;

    const selection = this.selections[index];
    const id = selection?.model;
    const member = id === undefined ? undefined : this.room.get(id);

    if (member && !this.grab.isHeld(member.model.root)) {
      outline.show(member.model.root, isTrueSize(member));
      return;
    }

    // Held, or gone from the room since it was chosen. Either way it has stopped being an
    // answer, so drop it rather than leaving the clock to run down on nothing.
    selection?.clear();
    outline.hide();
  }

  /**
   * Which model in the room a hand is aimed at.
   *
   * A model in a hand is not a candidate. It is already being moved by that hand, and
   * driving it about with a stick at the same time would be two contradictory answers to
   * the same question.
   */
  private aimAt(controller: Object3D): AimedModel | undefined {
    const hit = this.pointer.probe(controller);
    if (!hit || this.grab.isHeld(hit.root)) return undefined;

    const member = this.memberFor(hit.root);
    return member ? { member, distance: hit.distance } : undefined;
  }

  private memberFor(root: Object3D): RoomMember | undefined {
    for (const member of this.room.values()) {
      if (member.model.root === root) return member;
    }
    return undefined;
  }

  /**
   * Move, turn or resize whatever each hand is aimed at.
   *
   * The two hands do different jobs. The right one moves a model about the room, the left
   * one turns it where it stands, and pressing either stick in resizes instead — so the
   * three gestures a model needs are reachable without a mode to remember, and each hand
   * has a single thing its stick does. A session with no left controller simply has no
   * turning gesture: better than putting it on an arbitrary hand and having it move about
   * as controllers connect.
   *
   * Each hand acts on its own target, so two models can be arranged at once — the same
   * independence grabbing already has. Two hands on the same model is fine now that they
   * do different things to it; two hands doing the *same* thing to it is what `claim`
   * refuses.
   */
  private nudgeAimed(seconds: number): void {
    if (this.menu.isOpen || seconds <= 0) return;

    const camera = this.scene.renderer.xr.getCamera();
    camera.getWorldPosition(this.scratchPosition);
    const viewer: Vec3 = [
      this.scratchPosition.x,
      this.scratchPosition.y,
      this.scratchPosition.z,
    ];

    this.claimed.clear();

    for (let index = 0; index < CONTROLLER_COUNT; index += 1) {
      const source = this.inputSources[index];
      const gamepad = source?.gamepad ?? undefined;
      const resizing = isButtonPressed(gamepad, THUMBSTICK_BUTTON);
      const turning = source?.handedness === 'left';
      const stick = readStick(gamepad);

      // A stick back at rest is a hand letting go, and the next push is free to choose
      // another model. It is also when a resize is finished, and therefore worth storing.
      if (stick.x === 0 && stick.forward === 0) {
        const finished = this.latched[index];
        if (finished) this.reportScale(finished);

        this.latched[index] = undefined;
        this.dwell[index] = 0;
        continue;
      }

      const member = this.latched[index] ?? this.aimed[index]?.member;
      if (!member) continue;
      if (!resizing) this.dwell[index] = 0;

      this.latched[index] = member;

      // Driving a model is the plainest statement of interest there is in one, so the box
      // stays up for as long as the stick is pushed and for the usual moment afterwards.
      this.selections[index]?.choose(member.model.id);

      if (resizing) {
        if (this.claim(member, 'resize'))
          this.resize(index, member, stick.forward, seconds);
      } else if (turning) {
        if (this.claim(member, 'turn')) this.turn(member, viewer, stick, seconds);
      } else if (this.claim(member, 'move')) {
        this.reposition(member, viewer, stick, seconds);
      }
    }

    // The one light casts the one shadow for the whole room, so a model that has moved,
    // turned or changed size has changed what that shadow has to cover.
    if (this.claimed.size > 0) this.scene.frameLighting();
  }

  /** Take one kind of change to one model for this frame, refusing a second claim on it. */
  private claim(member: RoomMember, change: string): boolean {
    const key = `${member.model.id}:${change}`;
    if (this.claimed.has(key)) return false;

    this.claimed.add(key);
    return true;
  }

  /**
   * Push, pull or swing a model round the viewer.
   *
   * The room's record is updated alongside the object rather than read back off it later,
   * so that putting another model out does not undo the arranging.
   */
  private reposition(
    member: RoomMember,
    viewer: Vec3,
    stick: Stick,
    seconds: number,
  ): void {
    const { root } = member.model;

    // Local is world here: a model not in a hand is parented to the scene itself, and one
    // in a hand was never a candidate.
    member.position = nudgePosition(
      [root.position.x, root.position.y, root.position.z],
      viewer,
      stick,
      seconds,
    );
    root.position.set(...member.position);
  }

  /**
   * Spin or tip a model where it stands.
   *
   * About its own origin, which the geometry was recentred onto when it was built: the
   * middle of its footprint, at the height it rests on. So a model spins in place rather
   * than orbiting some point off to one side, and tipping it works the way tipping
   * something standing on a table does.
   */
  private turn(member: RoomMember, viewer: Vec3, stick: Stick, seconds: number): void {
    const { root } = member.model;
    const { x, y, z, w } = root.quaternion;

    // Local is world here, for the same reason as in `reposition`: a model in a hand was
    // never a candidate, and one that is not is parented to the scene itself.
    member.rotation = nudgeRotation(
      [x, y, z, w],
      [root.position.x, root.position.y, root.position.z],
      viewer,
      stick,
      seconds,
    );
    root.quaternion.set(...member.rotation);
  }

  /** Grow or shrink a model, buzzing the hand when it settles back onto its true size. */
  private resize(
    index: number,
    member: RoomMember,
    forward: number,
    seconds: number,
  ): void {
    const { root } = member.model;
    const result = nudgeScale(
      root.scale.x,
      MM_TO_METRES,
      forward,
      seconds,
      this.dwell[index] ?? 0,
    );

    this.dwell[index] = result.dwell;
    member.scale = result.scale;
    root.scale.setScalar(result.scale);

    if (result.settled) this.pulse(index);
  }

  /**
   * Buzz one controller.
   *
   * Fire and forget: the promise reports when the buzz has finished, which is of no
   * interest, and hardware without haptics is a reason to feel nothing rather than a
   * reason to fail.
   */
  private pulse(index: number): void {
    const actuator = this.inputSources[index]?.gamepad?.hapticActuators[0];
    void actuator?.pulse(DETENT_PULSE, DETENT_PULSE_MS).catch(() => undefined);
  }

  /**
   * Hand a finished resize to the application to store, if it changed anything.
   *
   * Guarded on the last figure reported rather than fired unconditionally, so that pushing
   * a model about — which ends the same way, with a stick returning to centre — does not
   * rewrite a record that says exactly what it already said.
   */
  private reportScale(member: RoomMember): void {
    const ratio = ratioOf(member);
    if (ratio === member.reported) return;

    member.reported = ratio;
    this.callbacks.onResize(member.model.id, ratio);
  }

  /** Drop every hand's aim, for entering, leaving, and laying the room out afresh. */
  private forgetAim(): void {
    for (let index = 0; index < CONTROLLER_COUNT; index += 1) {
      this.aimed[index] = undefined;
      this.latched[index] = undefined;
      this.dwell[index] = 0;
      this.selections[index]?.clear();
      this.outlines[index]?.hide();
    }
  }

  private createControllers(): void {
    const factory = new XRControllerModelFactory();

    for (let index = 0; index < CONTROLLER_COUNT; index += 1) {
      const controller = this.scene.renderer.xr.getController(index);
      controller.visible = false;

      // The runtime hands over the input source on connection. That is the only reliable
      // way to reach the gamepad, whose face buttons raise no events of their own.
      controller.addEventListener('connected', (event: { data?: XRInputSource }) => {
        this.inputSources[index] = event.data;
      });
      controller.addEventListener('disconnected', () => {
        this.inputSources[index] = undefined;
      });

      controller.addEventListener('selectstart', this.handleTriggerStart);
      controller.addEventListener('selectend', this.handleGrabEnd);
      controller.addEventListener('squeezestart', this.handleGrabStart);
      controller.addEventListener('squeezeend', this.handleGrabEnd);

      const ray = createRay();
      ray.visible = false;
      controller.add(ray);
      this.rays.push(ray);

      // The outline belongs to the scene rather than to the controller: it is drawn round
      // a model standing somewhere in the room, not round anything the hand is carrying.
      const outline = new Outline();
      this.scene.scene.add(outline.object);
      this.outlines.push(outline);

      this.scene.scene.add(controller);
      this.controllers.push(controller);

      // The grip space tracks the physical controller, unlike the target-ray space above
      // it, so this is both where the rendered controller belongs and the position a
      // grab should be measured from.
      const grip = this.scene.renderer.xr.getControllerGrip(index);
      grip.visible = false;
      grip.add(factory.createControllerModel(grip));
      this.scene.scene.add(grip);
      this.grips.push(grip);
    }
  }

  /**
   * The trigger takes hold of a model within reach, and picks out one that is not.
   *
   * Three jobs on one button, and they do not overlap. The open menu comes first, since the
   * panel intercepts the ray the way a solid object would: a pull aimed at it clicks, and
   * one aimed anywhere else falls through. Then the grab, which needs the hand to be
   * actually inside the model. And a pull that found neither is a pull at something across
   * the room — which is what draws the box round it.
   *
   * That last is why the box is on the trigger rather than on the beam. Pointing happens
   * whether you mean it or not; pulling the trigger does not.
   */
  private readonly handleTriggerStart = (event: { target: Object3D }): void => {
    if (!this.active) return;

    if (this.menu.isOpen) {
      const hit = this.menu.probe(event.target);
      if (hit) {
        if (hit.region) this.menu.activate(hit.region);
        return;
      }
    }

    if (this.grabWith(event.target)) return;
    this.select(event.target);
  };

  private readonly handleGrabStart = (event: { target: Object3D }): void => {
    this.grabWith(event.target);
  };

  private readonly handleGrabEnd = (event: { target: Object3D }): void => {
    const grip = this.grips[this.handIndex(event.target)];
    if (grip) this.grab.release(grip);
  };

  /**
   * Take hold with this hand, if there is anything in reach. Reports whether it did.
   *
   * Grabbing measures from the grip, whichever button asked for it: the grip space sits
   * where the user's hand actually is, unlike the target ray, and reaching out to take hold
   * of something is the gesture being modelled.
   */
  private grabWith(controller: Object3D): boolean {
    if (!this.active) return false;

    const index = this.handIndex(controller);
    const grip = this.grips[index];
    if (!grip) return false;
    if (!this.grab.tryGrab(grip)) return false;

    // Directly grabbed, which is the one way of choosing a model that wants no box drawn
    // round it: it is in your hand, and the hand that took it has stopped pointing at
    // things.
    this.selections[index]?.clear();
    return true;
  }

  /**
   * Single out the model this hand is pointing at, from a trigger pull that grabbed nothing.
   *
   * Pointing at nothing is an answer too, and drops whatever was chosen — so the box can be
   * dismissed as deliberately as it was asked for, rather than only by waiting.
   */
  private select(controller: Object3D): void {
    const selection = this.selections[this.handIndex(controller)];
    if (!selection) return;

    const aim = this.aimAt(controller);
    if (aim) selection.choose(aim.member.model.id);
    else selection.clear();
  }

  /** Which of the two hands this is, or -1 for an object that is neither. */
  private handIndex(controller: Object3D): number {
    return this.controllers.indexOf(controller as Group);
  }

  /**
   * Take the hands out of the scene on the way out of a session.
   *
   * Only the hiding is done here. Whether a hand is visible *during* a session is three's
   * answer rather than ours: it decides afresh each frame from whether the runtime posed
   * that controller. But it only revisits the question for a controller the runtime has
   * told it about, so a hand shown on the way in stays shown for the rest of the session
   * — and a controller nobody is holding is never posed, which leaves it sitting at the
   * origin with its pointer lying flat along the floor.
   */
  private hideHands(): void {
    for (const controller of this.controllers) controller.visible = false;
    for (const grip of this.grips) grip.visible = false;
    for (const ray of this.rays) ray.visible = false;
  }
}

/**
 * Whether a model still measures what its file says it does.
 *
 * Read off the live object rather than off the record, so that it is true of the model in
 * front of you mid-gesture rather than of the one the room last agreed on.
 */
function isTrueSize(member: RoomMember): boolean {
  return settleRatio(member.model.root.scale.x / MM_TO_METRES) === TRUE_SIZE;
}

/** How much bigger or smaller than true size a model is currently standing. */
function ratioOf(member: RoomMember): number {
  return settleRatio(member.scale / MM_TO_METRES);
}

/** A model's extent as it will actually appear, still in the millimetres placement uses. */
function shownSize(bounds: Bounds, ratio: number): Vec3 {
  const [width, height, depth] = boundsSize(bounds);
  return [width * ratio, height * ratio, depth * ratio];
}

/** A thin line down the controller's pointing axis, scaled to reach whatever it hits. */
function createRay(): Line {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute([0, 0, 0, 0, 0, -1], 3));

  const material = new LineBasicMaterial({
    color: 0x4da3ff,
    transparent: true,
    opacity: 0.75,
    depthTest: false,
  });

  const line = new Line(geometry, material);
  line.renderOrder = 9;
  line.scale.z = RAY_LENGTH_M;
  return line;
}
